import React, { useState, useEffect, useContext } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { AuthContext } from '../../App';
import Button from '../ui/Button';
import Badge from '../ui/Badge';
import Table from '../ui/Table';
import { dataService } from '../../services/dataService';
import { LearningPath } from '../../data/types';
import PageContainer from '../layouts/PageContainer';
import TabContainer from '../layouts/TabContainer';
import ContentContainer from '../layouts/ContentContainer';

const SuperAdminLearningPathManageScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { activeRole } = useContext(AuthContext);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<'district' | 'shared'>('district');
  const [gradeFilter, setGradeFilter] = useState<string>('');
  const [difficultyFilter, setDifficultyFilter] = useState<string>('');
  const [standardFilter, setStandardFilter] = useState<string>('');
  const [loading, setLoading] = useState(true);
  
  // Track the current page and handle role-specific redirects
  useEffect(() => {
    // Store the current page in sessionStorage
    sessionStorage.setItem('lastViewedPage', location.pathname);
    
    // Redirect to the appropriate role-specific learning path page if needed
    if (activeRole === 'Teacher') {
      navigate('/learning-paths/manage', { replace: true });
    } else if (activeRole === 'SchoolAdmin') {
      navigate('/schooladmin/learning-paths', { replace: true });
    }
  }, [activeRole, location.pathname, navigate]);
  
  // State for learning paths
  const [districtPaths, setDistrictPaths] = useState<LearningPath[]>([]);
  const [sharedPaths, setSharedPaths] = useState<LearningPath[]>([]);

  // Fetch learning paths from dataService
  useEffect(() => {
    const fetchLearningPaths = async () => {
      try {
        setLoading(true);
        const paths = await dataService.getLearningPaths();
        
        // Filter paths into district and shared categories
        const district = paths.filter(path => path.isDistrict);
        const shared = paths.filter(path => path.isShared && !path.isDistrict);
        
        setDistrictPaths(district);
        setSharedPaths(shared);
      } catch (error) {
        console.error('Error fetching learning paths:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLearningPaths();
  }, []);

  // Handle path selection
  const handlePathSelect = (pathId: string) => {
    if (activeTab === 'district') {
      // Navigate to edit screen for district path
      // Use replace: true to prevent flashing of cards during navigation
      navigate(`/superadmin/learning-paths/edit/${pathId}`, { replace: true });
    } else {
      // Clone the shared path as a district template
      const pathToClone = sharedPaths.find(path => path.id === pathId);
      if (pathToClone) {
        // In a real app, this would create a new path based on the selected one
        // For now, just navigate to the designer with a query param
        // Use replace: true to prevent flashing of cards during navigation
        navigate(`/superadmin/learning-paths/create?cloneFrom=${pathId}`, { replace: true });
      }
    }
  };

  // Create new path
  const handleCreateNewPath = () => {
    // Use replace: true to prevent flashing of cards during navigation
    navigate('/superadmin/learning-paths/create', { replace: true });
  };

  // Handle path deletion (would be implemented with a confirmation dialog in a real app)
  const handleDeletePath = (pathId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    // In a real app, this would call an API to delete the path
    alert(`Path ${pathId} would be deleted. This is just a mockup.`);
  };

  // Reset filters
  const resetFilters = () => {
    setSearchQuery('');
    setGradeFilter('');
    setDifficultyFilter('');
    setStandardFilter('');
  };

  // Get active paths based on tab
  const getActivePaths = () => {
    if (activeTab === 'district') {
      return districtPaths;
    } else {
      return sharedPaths;
    }
  };

  // Apply filters to paths
  const getFilteredPaths = () => {
    let filteredPaths = getActivePaths();
    
    // Apply search query filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filteredPaths = filteredPaths.filter(path => 
        path.name.toLowerCase().includes(query) || 
        path.description.toLowerCase().includes(query)
      );
    }
    
    // Apply grade filter
    if (gradeFilter) {
      filteredPaths = filteredPaths.filter(path => path.grade === gradeFilter);
    }
    
    // Apply difficulty filter
    if (difficultyFilter) {
      filteredPaths = filteredPaths.filter(path => path.difficulty === difficultyFilter);
    }
    
    // Apply standard filter - this is a mock implementation
    if (standardFilter) {
      filteredPaths = filteredPaths.filter(path => {
        // In a real app, this would check if the path contains standards of a specific type
        // For now, just filter based on having any standards at all
        return path.standards.length > 0;
      });
    }
    
    return filteredPaths;
  };

  // Get color for difficulty badge
  const getDifficultyColor = (difficulty: 'Easy' | 'Medium' | 'Hard'): 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' => {
    switch (difficulty) {
      case 'Easy':
        return 'success';
      case 'Medium':
        return 'warning';
      case 'Hard':
        return 'error';
      default:
        return 'primary';
    }
  };

  const createPathButton = (
    <Button
      variant="primary"
      onClick={handleCreateNewPath}
    >
      <span className="flex items-center">
        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
        </svg>
        Create New Path
      </span>
    </Button>
  );

  return (
    <PageContainer title="Learning Paths" actionButton={createPathButton} userRole="SuperAdmin">
      
      <TabContainer
        tabContent={
          <div className="flex space-x-4 py-2">
            <button
              className={`px-4 py-2 font-medium text-sm rounded-md ${activeTab === 'district' ? 'bg-primary text-white' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('district')}
            >
              District Paths
            </button>
            <button
              className={`px-4 py-2 font-medium text-sm rounded-md ${activeTab === 'shared' ? 'bg-primary text-white' : 'text-gray-500 hover:text-gray-700'}`}
              onClick={() => setActiveTab('shared')}
            >
              Shared Paths
            </button>
          </div>
        }
        filterContent={
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search paths..."
                className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
              <svg
                className="absolute left-3 top-2.5 h-5 w-5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            
            <div>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                value={gradeFilter}
                onChange={(e) => setGradeFilter(e.target.value)}
              >
                <option value="">All Grades</option>
                {['K', '1', '2', '3', '4', '5', '6', '7', '8'].map(grade => (
                  <option key={grade} value={grade}>
                    {grade === 'K' ? 'Kindergarten' : `Grade ${grade}`}
                  </option>
                ))}
              </select>
            </div>
            
            <div>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                value={difficultyFilter}
                onChange={(e) => setDifficultyFilter(e.target.value)}
              >
                <option value="">All Difficulty Levels</option>
                <option value="Easy">Easy</option>
                <option value="Medium">Medium</option>
                <option value="Hard">Hard</option>
              </select>
            </div>
            
            <div>
              <select
                className="w-full border border-gray-300 rounded-md px-3 py-2"
                value={standardFilter}
                onChange={(e) => setStandardFilter(e.target.value)}
              >
                <option value="">All Standards</option>
                {dataService.getDomains().map(domain => (
                  <option key={domain.id} value={domain.id}>{domain.name}</option>
                ))}
              </select>
            </div>
            
            <div>
              <button
                className="px-4 py-2 text-sm text-gray-600 bg-gray-100 rounded-md hover:bg-gray-200"
                onClick={resetFilters}
              >
                Reset Filters
              </button>
            </div>
          </div>
        }
      >
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            <p className="mt-2 text-gray-500">Loading learning paths...</p>
          </div>
        ) : (
          <>
            {activeTab === 'district' && (
              <div className="overflow-x-auto max-h-[calc(100vh-350px)]">
                <Table>
                  <thead>
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Standards</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Difficulty</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getFilteredPaths().length > 0 ? (
                      getFilteredPaths().map(path => (
                        <tr key={path.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div 
                              className="text-sm text-gray-900 cursor-pointer hover:text-primary"
                              onClick={() => handlePathSelect(path.id)}
                            >
                              {path.name}
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{path.description}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{path.grade}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{path.standards.length}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm">
                            <Badge variant={getDifficultyColor(path.difficulty ?? 'Medium')}>{path.difficulty ?? 'Medium'}</Badge>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-right">
                            {activeRole !== 'SchoolAdmin' && (
                              <>
                                <button
                                  className="px-2 py-1 text-xs text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors mr-2"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handlePathSelect(path.id);
                                  }}
                                >
                                  Edit
                                </button>
                                <button
                                  className="px-2 py-1 text-xs text-red-600 hover:text-red-800 bg-red-50 hover:bg-red-100 rounded-md transition-colors"
                                  onClick={(e) => handleDeletePath(path.id, e)}
                                >
                                  Delete
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="text-center py-8 text-gray-500">
                          No learning paths found matching your search.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            )}
            {activeTab === 'shared' && (
              <div className="overflow-x-auto max-h-[calc(100vh-350px)]">
                <Table>
                  <thead>
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Name</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Grade</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Standards</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Difficulty</th>
                      <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {getFilteredPaths().length > 0 ? (
                      getFilteredPaths().map(path => (
                        <tr key={path.id} className="hover:bg-gray-50">
                          <td className="px-3 py-2 whitespace-nowrap">
                            <div className="text-sm text-gray-900">
                              {path.name}
                            </div>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{path.description}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{path.grade}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{path.standards.length}</td>
                          <td className="px-3 py-2 whitespace-nowrap text-sm">
                            <Badge variant={getDifficultyColor(path.difficulty ?? 'Medium')}>{path.difficulty ?? 'Medium'}</Badge>
                          </td>
                          <td className="px-3 py-2 whitespace-nowrap text-right">
                            {activeRole !== 'SchoolAdmin' && (
                              <button
                                className="px-2 py-1 text-xs text-blue-600 hover:text-blue-800 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
                                onClick={() => handlePathSelect(path.id)}
                              >
                                Clone as District Template
                              </button>
                            )}
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={6} className="text-center py-8 text-gray-500">
                          No learning paths found matching your search.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>
            )}
          </>
        )}
      </TabContainer>
    </PageContainer>
  );
};

export default SuperAdminLearningPathManageScreen;
